import Vue from 'vue';
import type { SelectionState } from '@/store';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import type { NodeRunResultSelectionMode } from '@/views/Runnits/constants';

export const GALLERY_SELECTION_CONTEXT_KEY = 'gallerySelectionContext';

export type SelectionLookupMap = Readonly<Record<string, true>>;

export interface GallerySelectionRenderContext {
	triggeringId: string | null;
	mode: NodeRunResultSelectionMode | null;
	canSelectTypes: SelectionLookupMap;
	selectedNodeRunResultUuids: SelectionLookupMap;
	selectedUploadIds: SelectionLookupMap;
	selectedAvatarIds: SelectionLookupMap;
	fingerprint: string;
}

const EMPTY_LOOKUP = Object.freeze({}) as SelectionLookupMap;

function makeLookupMap (values: Array<string | null | undefined>): SelectionLookupMap {
	if (!values.length) return EMPTY_LOOKUP;

	const map: Record<string, true> = {};
	values.forEach((value) => {
		if (value) {
			map[value] = true;
		}
	});

	return Object.freeze(map);
}

export function createGallerySelectionRenderContext (): GallerySelectionRenderContext {
	return Vue.observable({
		triggeringId: null,
		mode: null,
		canSelectTypes: EMPTY_LOOKUP,
		selectedNodeRunResultUuids: EMPTY_LOOKUP,
		selectedUploadIds: EMPTY_LOOKUP,
		selectedAvatarIds: EMPTY_LOOKUP,
		fingerprint: '',
	}) as GallerySelectionRenderContext;
}

function resetGallerySelectionRenderContext (context: GallerySelectionRenderContext): void {
	context.triggeringId = null;
	context.mode = null;
	context.canSelectTypes = EMPTY_LOOKUP;
	context.selectedNodeRunResultUuids = EMPTY_LOOKUP;
	context.selectedUploadIds = EMPTY_LOOKUP;
	context.selectedAvatarIds = EMPTY_LOOKUP;
	context.fingerprint = '';
}

export function buildGallerySelectionFingerprint (selectionState: SelectionState | null | undefined): string {
	if (!selectionState || !selectionState.sortOrder || !selectionState.nodeRunResultSelectionMode) {
		return '';
	}

	const selectedNodeRunResultUuids = (selectionState.selectedNodeRunResults || [])
		.map((result) => result?.uuid)
		.filter(Boolean)
		.join(',');
	const selectedUploadIds = (selectionState.selectedUploads || [])
		.map((upload) => upload?.id)
		.filter(Boolean)
		.join(',');
	const selectedAvatarIds = (selectionState.selectedAvatars || [])
		.map((avatar) => avatar?.id)
		.filter(Boolean)
		.join(',');
	const canSelectTypes = (selectionState.canSelectTypes || []).join(',');

	return [
		selectionState.triggeringId || '',
		selectionState.nodeRunResultSelectionMode || '',
		canSelectTypes,
		selectedNodeRunResultUuids,
		selectedUploadIds,
		selectedAvatarIds,
	].join('|');
}

export function updateGallerySelectionRenderContext (
	context: GallerySelectionRenderContext,
	selectionState: SelectionState | null | undefined,
): void {
	const fingerprint = buildGallerySelectionFingerprint(selectionState);
	if (context.fingerprint === fingerprint) return;

	if (!fingerprint || !selectionState) {
		resetGallerySelectionRenderContext(context);
		return;
	}

	context.triggeringId = selectionState.triggeringId || null;
	context.mode = selectionState.nodeRunResultSelectionMode || null;
	context.canSelectTypes = makeLookupMap(selectionState.canSelectTypes || []);
	context.selectedNodeRunResultUuids = makeLookupMap(
		(selectionState.selectedNodeRunResults || []).map((result) => result?.uuid),
	);
	context.selectedUploadIds = makeLookupMap(
		(selectionState.selectedUploads || []).map((upload) => upload?.id),
	);
	context.selectedAvatarIds = makeLookupMap(
		(selectionState.selectedAvatars || []).map((avatar) => avatar?.id),
	);
	context.fingerprint = fingerprint;
}

export function isGallerySelectionMode (
	context: GallerySelectionRenderContext | null | undefined,
	type: string | null | undefined,
	noClick: boolean,
): boolean {
	if (!context || noClick || !context.mode || !type) return false;
	return !!context.canSelectTypes[type];
}

export function isGallerySingleSelection (context: GallerySelectionRenderContext | null | undefined): boolean {
	return !!context && context.mode === NODE_RUN_RESULT_SELECTION_MODE.SINGLE;
}

export function isGalleryMultiSelection (context: GallerySelectionRenderContext | null | undefined): boolean {
	return !!context && context.mode === NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE;
}

export function isSelectedInGalleryContext (
	context: GallerySelectionRenderContext | null | undefined,
	payload: {
		nodeRunResultUuid?: string | null;
		uploadId?: string | null;
		avatarId?: string | null;
	},
): boolean {
	if (!context) return false;

	return !!(
		(payload.nodeRunResultUuid && context.selectedNodeRunResultUuids[payload.nodeRunResultUuid]) ||
		(payload.uploadId && context.selectedUploadIds[payload.uploadId]) ||
		(payload.avatarId && context.selectedAvatarIds[payload.avatarId])
	);
}
